/**
 * Crash reports, the runtime part (the pure part, redaction and the report itself, is lib/crashReport.ts).
 *
 * `installCrashCapture()` (main.tsx, the app only, not the website's live demo):
 *  - keeps the last console errors and warnings in a small ring buffer, redacted, in this tab's memory only;
 *  - notes uncaught errors and unhandled rejections there too. One uncaught error that keeps coming (the 3D view's
 *    frame loop throwing every frame) opens the crash screen; so does an error while React draws the app
 *    (`CrashBoundary`, ui/CrashScreen.tsx).
 *
 * The crash screen shows the whole report first. Collecting it reads only this tab's state; nothing leaves until the
 * person presses "Send to Lab43": then one request to auth.lab43.dev (never Supabase, never our server), no retries.
 */
import { create } from "zustand";
import { isDesktop } from "./desktop.ts";
import {
  buildCrashReport, crashRequestBody, MAX_LOG_LINE, MAX_LOG_LINES, redact, sendCrashReport,
  type CrashFacts, type CrashReport, type SendResult, type UaHints, type WebGlFacts,
} from "./crashReport.ts";

/** set by vite.config.ts (`define`); missing in other builds (tests, the website's demo) */
declare const __MCP_VERSION__: string | undefined;
declare const __MCP_BUILD__: string | undefined;

export const APP_VERSION = typeof __MCP_VERSION__ === "string" && __MCP_VERSION__ ? __MCP_VERSION__ : "dev";
export const APP_BUILD = typeof __MCP_BUILD__ === "string" ? __MCP_BUILD__ : "";

export interface CrashInfo {
  where: "react" | "repeated";
  error: { name: string; message: string; stack?: string };
  componentStack?: string;
  repeats?: number;
}

interface CrashState {
  /** the crash the screen is showing (null: none) */
  info: CrashInfo | null;
  set: (p: Partial<CrashState>) => void;
}

export const useCrash = create<CrashState>((set) => ({ info: null, set: (p) => set(p) }));

/** whatever was thrown, as name + message + stack (a thrown string or object too) */
export function errorLike(e: unknown): CrashInfo["error"] {
  if (e instanceof Error) return { name: e.name || "Error", message: e.message ?? "", stack: typeof e.stack === "string" ? e.stack : undefined };
  if (typeof e === "string") return { name: "Error", message: e };
  if (e && typeof e === "object" && "message" in e) return { name: String((e as { name?: unknown }).name ?? "Error"), message: String((e as { message: unknown }).message) };
  return { name: "Error", message: `thrown ${e === null ? "null" : typeof e}` };
}

// ---------------------------------------------------------------- the ring buffer

const log: string[] = [];
const started = typeof performance !== "undefined" ? performance.now() : 0;
const since = () => ((performance.now() - started) / 1000).toFixed(1);

/** one console argument as text: never an object's contents (it could be plan data), only its kind */
function argText(a: unknown): string {
  if (typeof a === "string") return a;
  if (a === null || a === undefined || typeof a === "number" || typeof a === "boolean" || typeof a === "bigint") return String(a);
  if (a instanceof Error) return `${a.name}: ${a.message}`;
  if (Array.isArray(a)) return `[array of ${a.length}]`;
  if (typeof a === "function") return "[function]";
  if (typeof a === "symbol") return "[symbol]";
  const name = (a as { constructor?: { name?: string } }).constructor?.name;
  return `[${name && name !== "Object" ? name : "object"}]`;
}

function remember(level: string, args: readonly unknown[]) {
  try {
    const text = args.map(argText).join(" ").replace(/%[csdifoO]/g, "").slice(0, 4 * MAX_LOG_LINE);
    log.push(`${since()} s ${level}: ${redact(text).slice(0, MAX_LOG_LINE)}`);
    if (log.length > MAX_LOG_LINES) log.splice(0, log.length - MAX_LOG_LINES);
  } catch { /* never let logging break the app */ }
}

/** the remembered lines, oldest first (a copy) */
export function recentLog(): string[] {
  return [...log];
}

// ---------------------------------------------------------------- uncaught errors

/** the same uncaught error this often within this time: the app can't go on by itself */
const REPEATS = 5;
const REPEAT_WINDOW_MS = 3000;
/** noise that repeats without anything being wrong */
const IGNORE = /ResizeObserver loop|^Script error\.?$/i;
const recent = new Map<string, number[]>();
/** errors whose crash screen was closed: they don't open it again */
const dismissed = new Set<string>();
/** the error the crash screen shows now */
let shownKey: string | null = null;

function onUncaught(e: ErrorEvent) {
  const message = e.message || (e.error instanceof Error ? e.error.message : "");
  if (IGNORE.test(message)) return;
  remember("uncaught", [e.error instanceof Error ? e.error : message]);
  const key = `${message}|${e.filename}:${e.lineno}`;
  const now = performance.now();
  const times = (recent.get(key) ?? []).filter((t) => now - t < REPEAT_WINDOW_MS);
  times.push(now);
  recent.set(key, times);
  if (recent.size > 50) recent.delete(recent.keys().next().value as string);
  const st = useCrash.getState();
  if (times.length >= REPEATS && !st.info && !dismissed.has(key)) {
    const err = e.error !== undefined && e.error !== null ? errorLike(e.error) : { name: "Error", message, stack: e.filename ? `    at ${e.filename}:${e.lineno}:${e.colno}` : undefined };
    shownKey = key;
    st.set({ info: { where: "repeated", error: err, repeats: times.length } });
  }
}

/** "Close" on the crash screen over the app: this error won't open it again */
export function dismissCrash() {
  if (shownKey) dismissed.add(shownKey);
  shownKey = null;
  useCrash.getState().set({ info: null });
}

/** Once per page, as early as possible (main.tsx); a flag on window, so a hot reload in dev doesn't wrap the console twice. */
export function installCrashCapture() {
  if (typeof window === "undefined") return;
  const w = window as Window & { __mcpCrashCapture?: boolean };
  if (w.__mcpCrashCapture) return;
  w.__mcpCrashCapture = true;
  for (const level of ["error", "warn"] as const) {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      original(...args);
      remember(level, args);
    };
  }
  window.addEventListener("error", onUncaught);
  window.addEventListener("unhandledrejection", (e) => remember("unhandled rejection", [e.reason instanceof Error ? e.reason : argText(e.reason)]));
}

/** The app stopped (error boundary): stop drawing the 3D view behind the crash screen. */
export function stopTheApp() {
  void import("../app/controller.ts").then(({ controller }) => controller.engine?.setPaused(true)).catch(() => {});
}

// ---------------------------------------------------------------- the facts

const within = <T,>(p: Promise<T>, ms: number, fallback: T) =>
  Promise.race([p.catch(() => fallback), new Promise<T>((r) => setTimeout(() => r(fallback), ms))]);

function webglOf(gl: WebGLRenderingContext | WebGL2RenderingContext): WebGlFacts {
  const p = (n: number) => { try { return gl.getParameter(n) as unknown; } catch { return undefined; } };
  const str = (v: unknown) => (typeof v === "string" ? v : undefined);
  const int = (v: unknown) => (typeof v === "number" ? v : undefined);
  let renderer = str(p(gl.RENDERER));
  let vendor = str(p(gl.VENDOR));
  // Chrome and Safari say "WebKit WebGL" there: the real GPU is behind this extension
  if (!renderer || /^(WebKit WebGL|Mozilla)$/i.test(renderer)) {
    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
    if (dbg) {
      renderer = str(p(dbg.UNMASKED_RENDERER_WEBGL)) ?? renderer;
      vendor = str(p(dbg.UNMASKED_VENDOR_WEBGL)) ?? vendor;
    }
  }
  const gl2 = typeof WebGL2RenderingContext !== "undefined" && gl instanceof WebGL2RenderingContext ? gl : null;
  return {
    version: str(p(gl.VERSION)),
    renderer,
    vendor,
    maxTextureSize: int(p(gl.MAX_TEXTURE_SIZE)),
    maxArrayLayers: gl2 ? int(p(gl2.MAX_ARRAY_TEXTURE_LAYERS)) : undefined,
    max3dTextureSize: gl2 ? int(p(gl2.MAX_3D_TEXTURE_SIZE)) : undefined,
    maxTextureUnits: int(p(gl.MAX_TEXTURE_IMAGE_UNITS)),
    maxRenderbufferSize: int(p(gl.MAX_RENDERBUFFER_SIZE)),
    contextLost: gl.isContextLost() || undefined,
  };
}

/** the 3D view's context, or (it never started) a throwaway one; null: this browser has no WebGL 2 */
function webglFacts(engineGl: WebGLRenderingContext | WebGL2RenderingContext | null): WebGlFacts | null {
  if (engineGl) return webglOf(engineGl);
  const gl = document.createElement("canvas").getContext("webgl2");
  if (!gl) return null;
  try {
    return webglOf(gl);
  } finally {
    gl.getExtension("WEBGL_lose_context")?.loseContext();
  }
}

/** the browser's own words for the system (Chromium only; also used by lib/feedback.ts) */
export async function uaHints(): Promise<UaHints | undefined> {
  const uad = (navigator as Navigator & {
    userAgentData?: { platform?: string; getHighEntropyValues?: (h: string[]) => Promise<Record<string, unknown>> };
  }).userAgentData;
  if (!uad?.getHighEntropyValues) return undefined;
  const v = await within(uad.getHighEntropyValues(["platformVersion", "architecture", "bitness"]), 800, {} as Record<string, unknown>);
  const s = (x: unknown) => (typeof x === "string" && x ? x : undefined);
  return { platform: s(uad.platform), platformVersion: s(v.platformVersion), architecture: s(v.architecture), bitness: s(v.bitness) };
}

/** the desktop app's version (null in a browser, "" when it can't be read; also used by lib/feedback.ts) */
export async function desktopVersion(): Promise<string | null> {
  if (!isDesktop()) return null;
  try {
    const { useUpdater } = await import("./updater.ts");
    const known = useUpdater.getState().current;
    if (known) return known;
    const { getVersion } = await import("@tauri-apps/api/app");
    return (await within(getVersion(), 1500, "")) || "";
  } catch {
    return "";
  }
}

/** the app's own fragment keys, whose values are its own words (#plans&ref=mod, #billing=success&plan=cloud) */
const APP_HASH_KEYS = new Set(["plans", "ref", "billing", "plan"]);

/** values of the address's fragment (#join=…&server=…, #invite=…): never in a report */
function hashValues(): string[] {
  try {
    return [...new URLSearchParams(location.hash.slice(1)).entries()].filter(([k, v]) => !APP_HASH_KEYS.has(k) && v.length >= 3).map(([, v]) => v);
  } catch {
    return [];
  }
}

/** worlds, zips and folders this tab opened or tried to: a failed open isn't in the app's state, but its name is private */
const opened = new Set<string>();

/** The controller notes the name of every world it opens (a zip's file name, a folder, a path), before reading it. */
export function noteOpenedName(name: string | null | undefined) {
  if (typeof name !== "string" || opened.size >= 200) return;
  const full = name.trim();
  if (full.length < 3) return;
  opened.add(full);
  const last = full.split(/[\\/]/).filter(Boolean).pop() ?? "";
  for (const n of [last, last.replace(/\.(?:zip|mcworld)$/i, "")]) if (n.length >= 3) opened.add(n);
}

/** Everything the report says, from this tab's state. Each part is optional: a broken part never stops the report. */
export async function collectFacts(info: CrashInfo): Promise<CrashFacts> {
  const secrets: (string | undefined | null)[] = [...hashValues(), ...opened];
  const facts: CrashFacts = {
    kind: "crash",
    where: info.where,
    error: info.error,
    componentStack: info.componentStack,
    repeats: info.repeats,
    app: { version: APP_VERSION, build: APP_BUILD || undefined, desktop: null },
    userAgent: navigator.userAgent,
    uptimeS: (performance.now() - started) / 1000,
    log: recentLog(),
    secrets: [],
  };
  const [hints, desktop] = await Promise.all([uaHints().catch(() => undefined), desktopVersion()]);
  facts.uaHints = hints;
  facts.app.desktop = desktop;
  try { facts.screen = { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio || 1 }; } catch { /* ignore */ }
  try {
    const mem = (performance as Performance & { memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number } }).memory;
    const nav = navigator as Navigator & { deviceMemory?: number };
    const mb = (b: number) => Math.round(b / 1024 / 1024);
    facts.memory = {
      ...(mem ? { usedMB: mb(mem.usedJSHeapSize), totalMB: mb(mem.totalJSHeapSize), limitMB: mb(mem.jsHeapSizeLimit) } : {}),
      ...(nav.deviceMemory ? { deviceGB: nav.deviceMemory } : {}),
      ...(navigator.hardwareConcurrency ? { cpus: navigator.hardwareConcurrency } : {}),
    };
  } catch { /* ignore */ }

  // the app's state (loaded already; dynamic imports keep this module light and the crash screen independent)
  const [store, ctl, auth] = await Promise.all([
    import("../state/store.ts").catch(() => null),
    import("../app/controller.ts").catch(() => null),
    import("./auth.ts").catch(() => null),
  ]);
  try { facts.webgl = webglFacts(ctl?.controller.engine?.renderer.getContext() ?? null); } catch { /* ignore */ }
  try {
    const a = auth?.useAccount.getState();
    secrets.push(a?.user?.id, a?.user?.email, a?.profile?.displayName);
  } catch { /* ignore */ }
  try {
    const s = store?.useApp.getState();
    if (s) {
      const here = s.dimensions.find((d) => d.id === s.dimension);
      facts.world = s.level || s.demo || s.dimensions.length
        ? {
            version: s.level?.version,
            dataVersion: s.level?.dataVersion,
            layout: s.dimensions.length ? (s.dimensions.some((d) => /(^|\/)dimensions\//.test(d.dir)) ? "dimensions" : "legacy") : undefined,
            modded: s.level?.modded,
            dimensions: s.dimensions.length,
            regionsHere: here?.regions.length,
            streamed: !!s.guestOf,
            mapColoursOnly: s.mapColoursOnly || undefined,
            demo: s.demo || undefined,
          }
        : null;
      facts.view = s.stats ? { ...s.stats, renderDistance: s.renderDistance } : null;
      const byKind: Record<string, number> = { path: 0, area: 0, building: 0, marker: 0 };
      const groups = new Set<string>();
      let schematicBuildings = 0;
      for (const o of s.plans) {
        byKind[o.kind] = (byKind[o.kind] ?? 0) + 1;
        if (o.group) groups.add(o.group);
        if (o.schematic) schematicBuildings++;
        secrets.push(o.name, o.groupName, o.schematic?.file, o.schematic?.author, o.sourceUrl, o.author);
        if (o.notes && o.notes.length <= 200) secrets.push(o.notes);
      }
      facts.plan = s.phase === "ready"
        ? {
            objects: s.plans.length,
            byKind,
            groups: groups.size,
            schematicBuildings,
            here: s.plans.filter((o) => o.dimension === s.dimension).length,
            readOnly: s.readOnly || undefined,
            role: s.room?.role,
            live: s.collab.status,
            peers: s.collab.peers.length,
          }
        : null;
      facts.ui = {
        phase: s.phase,
        tool: s.tool,
        panel: s.layoutPanel,
        board: !!s.boardFor || undefined,
        slice: s.cutEnabled || undefined,
        topDown: s.topDown || undefined,
        lighting: s.lighting.on || undefined,
        help: s.helpOpen || undefined,
      };
      // the names the app knows: world, folder, plans, schematics, templates, versions, people, the shared world
      secrets.push(s.level?.name, s.level?.seed, s.assetsLabel, s.worldId, s.guestOf, s.collab.name, s.collab.url);
      for (const d of s.dimensions) secrets.push(d.dir.split("/")[0]);
      for (const x of s.schematics) secrets.push(x.name, x.fileName, x.author);
      for (const t of [...s.worldTemplates, ...s.myTemplates]) secrets.push(t.name, t.author);
      for (const v of s.versions) secrets.push(v.name, v.author);
      for (const p of s.collab.peers) secrets.push(p.name, p.uid);
      if (s.room) secrets.push(s.room.id, s.room.name, s.room.ownerName);
      if (s.invite) secrets.push(s.invite.room, s.invite.server);
      if (s.accountInvite) secrets.push(s.accountInvite.token, s.accountInvite.server);
      if (s.home?.setBy) secrets.push(s.home.setBy);
    }
  } catch { /* ignore */ }
  // the website's own address is not a secret (it's in every stack frame)
  const own = new Set([location.origin, location.host, "app.mc-planner.com", "https://app.mc-planner.com"]);
  facts.secrets = [...new Set(secrets.filter((x): x is string => typeof x === "string" && x.length > 0 && !own.has(x)))].slice(0, 3000);
  return facts;
}

/** The report for the crash screen (collecting can't fail: missing parts are left out). */
export async function prepareReport(info: CrashInfo): Promise<CrashReport> {
  try {
    return buildCrashReport(await collectFacts(info));
  } catch {
    return buildCrashReport({
      kind: "crash", where: info.where, error: info.error, componentStack: info.componentStack, repeats: info.repeats,
      app: { version: APP_VERSION, build: APP_BUILD || undefined, desktop: isDesktop() ? "" : null },
      userAgent: navigator.userAgent, uptimeS: (performance.now() - started) / 1000, log: recentLog(), secrets: [...hashValues(), ...opened],
    });
  }
}

/** "Send to Lab43": once, on the click. */
export function sendReport(report: CrashReport, note: string): Promise<SendResult> {
  return sendCrashReport(crashRequestBody(report, note));
}
